import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSession } from '@/hooks/useSession';
import { useSurahList, useSurah } from '@/hooks/useSurah';
import {
  useTotalAyahReads,
  useReadingProgress,
  useReadsByAllSurahs,
  useReadingStreak,
  useBookmarksCount,
} from '@/hooks/useStudentStats';
import { IconArrowRight } from '@/components/home/icons';

/**
 * /streak — Student Insights dashboard.
 *
 * Every metric on this page is derived from real Supabase data — no
 * hardcoded fixtures. Sections that previously showed fake info
 * (Peer Rankings, Hall of Fame achievements) were removed because
 * those tables don't exist yet.
 *
 * Data sources (all RLS-filtered to the signed-in user):
 *   - Course Mastery   → total ayah_reads / 6 236 (Quran total)
 *   - Daily Momentum   → consecutive-day streak from ayah_reads.read_at
 *   - Current Target   → reading_progress.last_surah_id + name + % done
 *   - Surahs in Progress → ayah_reads grouped by surah, joined to surahs
 *   - Reading Activity → bookmarks count, distinct surahs, longest streak
 *
 * Signed-out users see a calm sign-in prompt instead of zero counters,
 * so the page doesn't look broken.
 */

const QURAN_TOTAL_AYAHS = 6236;

export function Streak() {
  useDocumentTitle('Student Insights');

  const session = useSession();
  const isSignedIn = !!session.data;

  // --- Real data hooks (all gated on signed-in) ------------------------
  const totalReadsQuery = useTotalAyahReads();
  const progressQuery = useReadingProgress();
  const surahReadsQuery = useReadsByAllSurahs();
  const streakQuery = useReadingStreak();
  const bookmarksQuery = useBookmarksCount();
  const surahListQuery = useSurahList();

  const currentSurahId = progressQuery.data?.last_surah_id ?? null;
  const currentSurahQuery = useSurah(currentSurahId);

  // --- Derived values --------------------------------------------------
  const totalReads = totalReadsQuery.data ?? 0;
  const masteryPct = Math.min(
    100,
    Math.round((totalReads / QURAN_TOTAL_AYAHS) * 100 * 10) / 10,
  );
  const streak = streakQuery.data ?? { current: 0, longest: 0, lastReadDate: null };
  const bookmarks = bookmarksQuery.data ?? 0;
  const surahsTouched = surahReadsQuery.data?.size ?? 0;

  // Current target: lookup the surah in the loaded list to get verse count.
  const currentSurahReadCount = useMemo(() => {
    if (!currentSurahId || !surahReadsQuery.data) return 0;
    return surahReadsQuery.data.get(currentSurahId) ?? 0;
  }, [currentSurahId, surahReadsQuery.data]);

  // In-progress surahs ordered by completion %, capped to 8 for cleanliness.
  const inProgressSurahs = useMemo(() => {
    if (!surahReadsQuery.data || !surahListQuery.data) return [];
    const byId = new Map(surahListQuery.data.map((s) => [s.id, s]));
    const rows: {
      id: number;
      name_en: string;
      total: number;
      read: number;
      pct: number;
    }[] = [];
    for (const [surahId, readCount] of surahReadsQuery.data.entries()) {
      const s = byId.get(surahId);
      if (!s) continue;
      const pct = Math.round((readCount / s.ayah_count) * 100);
      rows.push({
        id: surahId,
        name_en: s.name_en,
        total: s.ayah_count,
        read: readCount,
        pct,
      });
    }
    return rows.sort((a, b) => b.pct - a.pct || a.id - b.id).slice(0, 8);
  }, [surahReadsQuery.data, surahListQuery.data]);

  // Hero subtitle — honest message based on real state.
  const heroSubtitle = !isSignedIn
    ? 'Sign in to see your real progress through the Quran.'
    : streak.current > 0
      ? `You're on a ${streak.current}-day reading streak. Keep going.`
      : totalReads > 0
        ? `Welcome back. Open a surah and mark an ayah to start a new streak.`
        : 'Welcome. Start by opening a surah and marking your first ayah as read.';

  // Resume URL — points at the user's last position, or Al-Fatihah 1:1.
  const resumeHref =
    progressQuery.data
      ? `/reader?surah=${progressQuery.data.last_surah_id}&ayah=${progressQuery.data.last_ayah_number}`
      : '/reader?surah=1&ayah=1';

  return (
    <div className="relative min-h-screen bg-night-900 text-night-50">
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -right-24 top-1/4 h-96 w-96 rounded-full bg-beam/5 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed bottom-0 left-0 h-[500px] w-[500px] rounded-full bg-beam-soft/5 blur-[150px]"
      />

      <section className="relative mx-auto max-w-[1280px] space-y-12 px-6 py-12 lg:px-12">
        {/* Hero */}
        <div className="space-y-3">
          <h1 className="font-display text-4xl font-bold tracking-tight lg:text-5xl">
            Student Insights
          </h1>
          <p className="max-w-2xl text-night-200">{heroSubtitle}</p>
        </div>

        {!isSignedIn ? (
          <SignInPrompt />
        ) : (
          <>
            {/* Three real metric cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <CourseMasteryCard
                pct={masteryPct}
                totalReads={totalReads}
                isLoading={totalReadsQuery.isLoading}
              />
              <DailyMomentumCard
                current={streak.current}
                longest={streak.longest}
                isLoading={streakQuery.isLoading}
              />
              <CurrentTargetCard
                name={currentSurahQuery.data?.name_en ?? null}
                total={currentSurahQuery.data?.ayah_count ?? 0}
                read={currentSurahReadCount}
                hasProgress={!!progressQuery.data}
                resumeHref={resumeHref}
                isLoading={progressQuery.isLoading || currentSurahQuery.isLoading}
              />
            </div>

            {/* Surahs in progress + activity summary */}
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              <SurahsInProgress
                rows={inProgressSurahs}
                isLoading={surahReadsQuery.isLoading || surahListQuery.isLoading}
              />
              <ReadingActivity
                bookmarks={bookmarks}
                surahsTouched={surahsTouched}
                longestStreak={streak.longest}
                lastReadDate={streak.lastReadDate}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Top-row cards
 * ------------------------------------------------------------------ */

function CourseMasteryCard({
  pct,
  totalReads,
  isLoading,
}: {
  pct: number;
  totalReads: number;
  isLoading: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-night-500/20 bg-night-700 p-8 transition-all hover:border-beam hover:shadow-[0_0_25px_-5px_rgba(0,229,255,0.3)]">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-beam">
        Course Mastery
      </p>
      <h3 className="mb-6 font-display text-4xl font-bold">
        {isLoading ? '—' : `${pct}%`}
      </h3>
      <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-night-500/20">
        <div
          className="h-full rounded-full bg-beam shadow-[0_0_15px_-3px_rgba(0,229,255,0.6)] transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-night-200">
        {isLoading
          ? 'Loading…'
          : `${totalReads.toLocaleString()} of ${QURAN_TOTAL_AYAHS.toLocaleString()} ayahs read`}
      </p>
    </div>
  );
}

function DailyMomentumCard({
  current,
  longest,
  isLoading,
}: {
  current: number;
  longest: number;
  isLoading: boolean;
}) {
  // 7 boxes — the first `current` (clamped to 7) are solid, rest fade.
  // Quick visual cue for streak length without needing per-day data.
  const filled = Math.min(7, current);
  return (
    <div className="relative overflow-hidden rounded-xl border border-night-500/20 bg-night-700 p-8 transition-all hover:border-beam hover:shadow-[0_0_25px_-5px_rgba(0,229,255,0.3)]">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-beam">
        Daily Momentum
      </p>
      <div className="mb-6 flex items-baseline gap-2">
        <h3 className="font-display text-4xl font-bold">
          {isLoading ? '—' : current}
        </h3>
        <span className="text-sm font-semibold uppercase tracking-[0.12em] text-night-200">
          Day Streak
        </span>
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 7 }).map((_, idx) => (
          <div
            key={idx}
            className="h-8 w-full rounded-sm bg-beam transition-opacity"
            style={{ opacity: idx < filled ? 1 : 0.15 }}
          />
        ))}
      </div>
      {longest > current && (
        <p className="mt-3 text-[11px] uppercase tracking-wide text-night-300">
          Best streak: {longest} days
        </p>
      )}
    </div>
  );
}

function CurrentTargetCard({
  name,
  total,
  read,
  hasProgress,
  resumeHref,
  isLoading,
}: {
  name: string | null;
  total: number;
  read: number;
  hasProgress: boolean;
  resumeHref: string;
  isLoading: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-night-500/20 bg-night-700 p-8 transition-all hover:border-beam hover:shadow-[0_0_25px_-5px_rgba(0,229,255,0.3)]">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-beam">
        Current Target
      </p>
      <h3 className="mb-1 font-display text-2xl font-bold">
        {isLoading ? '—' : hasProgress ? (name ?? 'Surah') : 'Al-Fatihah'}
      </h3>
      <p className="mb-6 text-night-200">
        {isLoading
          ? 'Loading…'
          : hasProgress
            ? read + ' / ' + total + ' verses read'
            : 'No surah opened yet — start here.'}
      </p>
      <Link
        to={resumeHref}
        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-beam transition-all hover:gap-2"
      >
        {hasProgress ? 'Resume Session' : 'Start Reading'}
        <IconArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}

function SurahsInProgress({
  rows,
  isLoading,
}: {
  rows: { id: number; name_en: string; total: number; read: number; pct: number }[];
  isLoading: boolean;
}) {
  return (
    <section className="lg:col-span-8">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-beam">
            Your Reading
          </h2>
          <h3 className="font-display text-3xl font-bold">Surahs in Progress</h3>
        </div>
        <Link
          to="/student"
          className="text-sm text-night-200 underline underline-offset-4 transition-colors hover:text-beam"
        >
          View All
        </Link>
      </div>
      {isLoading ? (
        <p className="text-sm text-night-300">Loading your progress…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-night-500/30 bg-night-800 p-8 text-center">
          <p className="text-sm text-night-200">
            You haven&rsquo;t marked any ayahs as read yet.
          </p>
          <Link
            to="/reader?surah=1&ayah=1"
            className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-beam hover:gap-2"
          >
            Open Al-Fatihah
            <IconArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id}>
              <Link
                to={'/surah/' + row.id}
                className="block rounded-xl border border-night-500/10 bg-night-800 p-4 transition-colors hover:border-beam/40 hover:bg-night-700"
              >
                <div className="mb-2 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-night-500/20 font-mono text-xs font-bold text-night-200">
                      {String(row.id).padStart(2, '0')}
                    </span>
                    <span className="truncate text-sm font-semibold uppercase tracking-[0.12em] text-night-50">
                      {row.name_en}
                    </span>
                  </div>
                  <span className="shrink-0 rounded-full bg-beam/10 px-3 py-1 text-[10px] font-bold uppercase tracking-tight text-beam">
                    {row.pct}%
                  </span>
                </div>
                <div className="h-1 w-full overflow-hidden rounded-full bg-night-500/20">
                  <div
                    className="h-full rounded-full bg-beam transition-[width] duration-500"
                    style={{ width: Math.min(100, row.pct) + '%' }}
                  />
                </div>
                <p className="mt-2 text-[11px] uppercase tracking-wide text-night-300">
                  {row.read} of {row.total} verses
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReadingActivity({
  bookmarks,
  surahsTouched,
  longestStreak,
  lastReadDate,
}: {
  bookmarks: number;
  surahsTouched: number;
  longestStreak: number;
  lastReadDate: string | null;
}) {
  const stats: { label: string; value: string | number }[] = [
    { label: 'Bookmarks', value: bookmarks },
    { label: 'Surahs Touched', value: surahsTouched },
    { label: 'Longest Streak', value: longestStreak + 'd' },
  ];
  return (
    <section className="lg:col-span-4">
      <div className="mb-8">
        <h2 className="text-xs font-semibold uppercase tracking-[0.22em] text-beam">
          Activity
        </h2>
        <h3 className="font-display text-3xl font-bold">Reading Summary</h3>
      </div>
      <div className="rounded-2xl border border-night-500/20 bg-night-700 p-6">
        <ul className="space-y-4">
          {stats.map((s) => (
            <li
              key={s.label}
              className="flex items-center justify-between border-b border-night-500/15 pb-4 last:border-b-0 last:pb-0"
            >
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-night-200">
                {s.label}
              </span>
              <span className="font-display text-2xl font-bold text-beam">
                {s.value}
              </span>
            </li>
          ))}
        </ul>
        {lastReadDate && (
          <p className="mt-6 text-center text-[11px] uppercase tracking-wide text-night-300">
            Last read: {lastReadDate}
          </p>
        )}
        <Link
          to="/student"
          className="mt-6 block w-full rounded-xl border border-beam py-3 text-center text-xs font-bold uppercase tracking-[0.18em] text-beam transition-all hover:bg-beam hover:text-beam-ink active:scale-95"
        >
          Open Student Portal
        </Link>
      </div>
    </section>
  );
}

function SignInPrompt() {
  return (
    <div className="rounded-2xl border border-night-500/30 bg-night-800/60 p-10 text-center">
      <h2 className="font-display text-2xl font-bold">Sign in to see your insights</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-night-200">
        Your streak, course mastery, and current surah live in your account —
        sign in or create one to start tracking real progress.
      </p>
      <Link
        to="/login?next=/streak"
        className="mt-6 inline-flex items-center gap-2 rounded-lg bg-beam px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-beam-ink transition-all hover:scale-105"
      >
        Sign in
        <IconArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  );
}
